import React, { useState, useEffect } from 'react';

function Monitoring() {
  const [hosts, setHosts] = useState([]);
  const [vms, setVms] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [vmLoading, setVmLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHosts();
    fetchMetrics();
    fetchVms();
    const interval = setInterval(() => {
      fetchMetrics();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHosts = async () => {
    try {
      const res = await fetch('/api/hardware/hosts');
      const data = await res.json();
      // Get all Proxmox hosts only (NGX-MC goes in VMs section)
      const allHosts = (data.hosts || []).filter(h => 
        h.job.includes('Proxmox') || h.job.includes('node')
      );
      setHosts(allHosts);
    } catch (e) {
      console.error('Failed to fetch hosts:', e);
    }
  };

  const fetchMetrics = async () => {
    try {
      const res = await fetch('/api/hardware/metrics');
      const data = await res.json();
      if (data.hosts) {
        const metricsMap = {};
        data.hosts.forEach(h => {
          metricsMap[h.instance] = h;
        });
        setMetrics(metricsMap);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVms = async () => {
    try {
      setVmLoading(true);
      const res = await fetch('/api/hardware/vms');
      const data = await res.json();
      setVms(data.vms || []);
    } catch (e) {
      console.error('Failed to fetch VMs:', e);
    } finally {
      setVmLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );

  const getStatusColor = (host) => {
    if (host.isContainer) return 'bg-blue-500';
    if (!host.up) return 'bg-red-500';
    const cpu = parseFloat(metrics[host.instance]?.cpu || 0);
    const mem = parseFloat(metrics[host.instance]?.memory || 0);
    if (cpu > 90 || mem > 90) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getCpuClass = (cpu) => {
    if (cpu === 'N/A') return 'text-gray-400';
    const val = parseFloat(cpu || 0);
    if (val > 90) return 'text-red-600 dark:text-red-400';
    if (val > 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getMemClass = (mem) => {
    if (mem === 'N/A') return 'text-gray-400';
    const val = parseFloat(mem || 0);
    if (val > 90) return 'text-red-600 dark:text-red-400';
    if (val > 80) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  const formatNetwork = (bps) => {
    if (bps === null || bps === undefined) return '-';
    if (bps > 1000000) return (bps / 1000000).toFixed(1) + ' Mbps';
    if (bps > 1000) return (bps / 1000).toFixed(0) + ' Kbps';
    return bps + ' bps';
  };

  // Count hosts and VMs
  const totalHosts = hosts.length;
  const upHosts = hosts.filter(h => h.up || h.isContainer).length;
  const downHosts = totalHosts - upHosts;
  const proxmoxHosts = hosts.filter(h => !h.isContainer);
  const containerHosts = hosts.filter(h => h.isContainer);

  // VM counts
  const runningVMs = vms.filter(v => v.status === 'running').length;
  const stoppedVMs = vms.filter(v => v.status !== 'running').length;

  const formatUptime = (seconds) => {
    if (!seconds) return '-';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    const gb = bytes / 1024 / 1024 / 1024;
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(0)} MB`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          🖥️ Infrastructure Monitoring
        </h1>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalHosts}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Hosts</div>
        </div>
        <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{upHosts}</div>
          <div className="text-sm text-green-700 dark:text-green-300">Online</div>
        </div>
        <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">{downHosts}</div>
          <div className="text-sm text-red-700 dark:text-red-300">Offline</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {proxmoxHosts.length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Proxmox Hosts</div>
        </div>
      </div>

      {/* Proxmox Hosts Section */}
      {proxmoxHosts.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">☁️ Proxmox Hosts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {proxmoxHosts.map((host) => {
              const hostMetrics = metrics[host.instance] || {};
              const cpu = hostMetrics.cpu || '-';
              const mem = hostMetrics.memory || '-';
              const disk = hostMetrics.disk || '-';
              const load = hostMetrics.load || '-';
              const netIn = hostMetrics.networkIn;
              const netOut = hostMetrics.networkOut;
              const hostname = hostMetrics.hostname || host.cluster || 'Unknown';
              
              return (
                <div
                  key={host.instance}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
                >
                  {/* Header with cluster name */}
                  <div className={`${getStatusColor(host)} p-4 text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{host.cluster || host.job}</h3>
                        <p className="text-sm opacity-80">{host.instance}</p>
                      </div>
                      <div className="text-3xl">
                        {host.up ? '🟢' : '🔴'}
                      </div>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {/* CPU */}
                      <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <div className={`text-xl font-bold ${getCpuClass(cpu)}`}>
                          {cpu !== '-' ? `${cpu}%` : '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">CPU</div>
                      </div>

                      {/* Memory */}
                      <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <div className={`text-xl font-bold ${getMemClass(mem)}`}>
                          {mem !== '-' ? `${mem}%` : '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Memory</div>
                      </div>

                      {/* Disk */}
                      <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <div className={`text-xl font-bold ${parseFloat(disk) > 80 ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>
                          {disk !== '-' ? `${disk}%` : '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Disk</div>
                      </div>
                    </div>

                    {/* Additional Metrics */}
                    <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Load:</span>
                        <span className="font-mono">{load}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Host:</span>
                        <span className="font-mono truncate max-w-[120px]">{hostname}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">↓ In:</span>
                        <span className="font-mono">{formatNetwork(netIn)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">↑ Out:</span>
                        <span className="font-mono">{formatNetwork(netOut)}</span>
                      </div>
                    </div>

                    {/* Location & Link */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 border-t pt-3">
                      <span>📍 {host.location || 'Unknown'}</span>
                      <a
                        href={`http://192.168.10.24:3001/d/proxmox-${(host.cluster || 'unknown').toLowerCase()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600"
                      >
                        Grafana →
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* NGX-MC Container Section */}
      {containerHosts.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">📦 Containers & VMs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {containerHosts.map((host) => {
              const hostMetrics = metrics[host.instance] || {};
              const hostname = hostMetrics.hostname || 'NGX-MC';
              
              return (
                <div
                  key={host.instance}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="bg-blue-500 p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{hostname}</h3>
                        <p className="text-sm opacity-80">{host.instance}</p>
                      </div>
                      <div className="text-3xl">🟢</div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-center py-4">
                      <p className="text-gray-500 dark:text-gray-400">Container running on Orion VM</p>
                      <p className="text-sm mt-2">Docker Container</p>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 border-t pt-3">
                      <p>📍 {host.location || 'Orion VM'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Error display */}
      {error && (
        <div className="mt-6 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <h2 className="font-semibold text-red-800 dark:text-red-200 mb-2">⚠️ Error</h2>
          <p className="text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Virtual Machines Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">🖥️ Virtual Machines</h2>
          <div className="flex gap-4 text-sm">
            <span className="text-green-600 dark:text-green-400">● {runningVMs} running</span>
            <span className="text-gray-500 dark:text-gray-400">● {stoppedVMs} stopped</span>
          </div>
        </div>

        {vmLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : vms.length === 0 ? (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
            No VMs found or Proxmox API not accessible
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vms.map((vm) => (
              <div
                key={`${vm.host}-${vm.vmid}`}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
              >
                {/* Header */}
                <div className={`p-4 text-white ${vm.status === 'running' ? 'bg-green-500' : 'bg-gray-500'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate" title={vm.name}>{vm.name}</h3>
                      <p className="text-sm opacity-80">
                        {vm.isContainer ? 'Container' : `VMID: ${vm.vmid}`} · {vm.host}
                      </p>
                    </div>
                    <div className="text-2xl ml-2">
                      {vm.status === 'running' ? '🟢' : '🔴'}
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {/* CPU */}
                    <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <div className={`text-xl font-bold ${parseFloat(vm.cpu) > 80 ? 'text-red-500' : 'text-green-500'}`}>
                        {vm.cpu}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">CPU</div>
                    </div>

                    {/* Memory */}
                    <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <div className={`text-xl font-bold ${parseFloat(vm.memUsage) > 80 ? 'text-yellow-500' : 'text-blue-500'}`}>
                        {vm.memUsage}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Memory</div>
                    </div>

                    {/* Disk */}
                    <div className="text-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <div className={`text-xl font-bold ${parseFloat(vm.diskUsage) > 80 ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>
                        {vm.diskUsage}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Disk</div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Memory:</span>
                      <span className="font-mono">{formatBytes(vm.mem)} / {formatBytes(vm.maxmem)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Disk:</span>
                      <span className="font-mono">{formatBytes(vm.disk)} / {formatBytes(vm.maxdisk)}</span>
                    </div>
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">Uptime:</span>
                      <span className="font-mono">{formatUptime(vm.uptime)}</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 border-t pt-3 mt-3">
                    <span>📍 {vm.node}</span>
                    <span className={`px-2 py-1 rounded text-xs ${vm.status === 'running' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {vm.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Monitoring;
